import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Check, Download, ExternalLink, FileText, GraduationCap, PlayCircle } from 'lucide-react'
import toast from 'react-hot-toast'
import academyService from '@/services/academyService'
import cd2bCertificacionService from '@/services/cd2bCertificacionService'
import { useAuthStore } from '@/store/authStore'
import { conDetalle, enlaceDeLeccion, urlIncrustable } from '@/lib/leccionesCurso'
import PageContainer from '@/components/common/PageContainer'
import PageHeader from '@/components/common/PageHeader'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import { Skeleton } from '@/components/ui/skeleton'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'

const TIPO_LABEL = { video: 'Video', reading: 'Lectura', exercise: 'Ejercicio', quiz: 'Quiz', download: 'Descarga' }

/**
 * Mi aprendizaje (docs/120 §5.5/§7.2): ruta de estudio en 3 pasos (curso → practicar en la
 * empresa demo → examen) + cursos de Academy por track + documentación de referencia.
 *
 * NUNCA se gatea — estudiar está abierto para todo asesor, certificado o no (a diferencia de las
 * otras 3 pantallas de "Reventa cd-2b"), así que esta página no usa `useReventaHabilitada`.
 */
export default function ReventaAprendizaje() {
  const navigate = useNavigate()
  // Al asesor de cd-2b no se le habla de «CoreDesk» (la marca pública es cd-2b) ni de márgenes de reventa: él no revende.
  const esCd2b = useAuthStore((s) => s.partner)?.organizationType === 'cd2b_consultant'
  const [cargando, setCargando] = useState(true)
  const [cursos, setCursos] = useState([])
  const [enrollments, setEnrollments] = useState([])
  const [tieneInsignia, setTieneInsignia] = useState(false)
  const [cursoActivoId, setCursoActivoId] = useState(null)
  const [inscribiendo, setInscribiendo] = useState(null)
  // Lección abierta en el visor: { mi, li } (índices de módulo y lección del curso activo).
  const [abierta, setAbierta] = useState(null)

  // `silencioso`: recarga sin el esqueleto (al marcar una lección con el visor abierto, el video no se corta).
  const cargar = useCallback(async ({ silencioso = false } = {}) => {
    if (!silencioso) setCargando(true)
    try {
      const [cur, enr, ins] = await Promise.all([
        academyService.getGlobalCourses({ category: 'cd2b_consultor' }).catch(() => ({ courses: [] })),
        academyService.getMyStudentEnrollments().catch(() => ({ data: [] })),
        cd2bCertificacionService.misInsignias().catch(() => ({ data: { insignias: [] } })),
      ])
      // La lista viene sin los enlaces de las lecciones: el detalle de cada curso los trae
      // (ver lib/leccionesCurso). Son pocos cursos por track; si un detalle falla, quedan los títulos.
      const detalles = await Promise.all((cur?.courses || []).map((c) => academyService.getCourseDetail(c.id).catch(() => null)))
      const listaCursos = conDetalle(cur?.courses, detalles)
      setCursos(listaCursos)
      setEnrollments(enr?.data || [])
      setTieneInsignia((ins?.data?.insignias || []).some((i) => i.estado === 'vigente' || i.estado === 'por_vencer'))
      if (listaCursos.length && !cursoActivoId) setCursoActivoId(listaCursos[0].id)
    } catch {
      toast.error('No se pudo cargar tu ruta de estudio')
    } finally {
      setCargando(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => { cargar() }, [cargar])

  const enrollmentDe = useCallback((cursoId) => enrollments.find((e) => {
    const cid = typeof e.courseId === 'object' ? e.courseId?.id : e.courseId
    return cid === cursoId
  }), [enrollments])

  const cursoActivo = cursos.find((c) => c.id === cursoActivoId) || cursos[0] || null
  const enrollmentActivo = cursoActivo ? enrollmentDe(cursoActivo.id) : null

  const pasoUnoHecho = enrollments.some((e) => (e.progress?.percentage || 0) >= 100)

  const inscribirse = async (curso) => {
    setInscribiendo(curso.id)
    try {
      await academyService.enroll(curso.id)
      toast.success(`Inscrito en "${curso.title}"`)
      setCursoActivoId(curso.id)
      await cargar()
    } catch (e) {
      const code = e?.response?.data?.code
      if (code === 'COURSE_REQUIRES_PAYMENT') {
        toast.error('Este curso requiere pago — contacta a tu Country Manager.')
      } else {
        toast.error(e?.response?.data?.error || 'No se pudo inscribir')
      }
    } finally {
      setInscribiendo(null)
    }
  }

  const completarLeccion = async (curso, enrollment, moduleIndex, lessonIndex) => {
    if (!enrollment) return
    try {
      await academyService.markLessonComplete(enrollment.id || enrollment._id, moduleIndex, lessonIndex)
      await cargar({ silencioso: true })
    } catch {
      toast.error('No se pudo marcar la lección')
    }
  }

  // Documentos de referencia: lecciones tipo 'download' de TODOS los cursos de la ruta, juntas
  // (el PRD las trata como "consulta rápida", no una sección por curso).
  const documentos = useMemo(() => {
    const out = []
    for (const c of cursos) {
      for (const m of c.curriculum || []) {
        for (const l of m.lessons || []) {
          if (l.type === 'download') out.push({ curso: c.title, ...l })
        }
      }
    }
    return out
  }, [cursos])

  const lessonDone = (enrollment, moduleIndex, lessonIndex) =>
    !!enrollment?.progress?.completedLessons?.some((l) => l.moduleIndex === moduleIndex && l.lessonIndex === lessonIndex)

  if (cargando) {
    return (
      <PageContainer>
        <Skeleton className="h-8 w-56" />
        <Skeleton className="h-40" />
      </PageContainer>
    )
  }

  return (
    <PageContainer>
      <PageHeader
        title="Mi aprendizaje"
        description="Todo lo que necesitas antes de presentar un examen — documentación, un curso corto por especialidad y tu propia empresa demo para practicar sin riesgo."
      />

      <div className="grid gap-3 sm:grid-cols-3">
        <PasoRuta n={1} titulo="Estudia el curso" desc="El de tu track, con lecciones cortas" hecho={pasoUnoHecho} />
        <PasoRuta n={2} titulo="Practica en tu empresa demo" desc="Cotiza, vende y configura sin que nada se facture"
          accion={<Button size="sm" variant="outline" onClick={() => navigate('/clientes')}>Abrir</Button>} />
        <PasoRuta n={3} titulo="Presenta el examen" desc="Cuando te sientas listo — varios intentos, sin apuro" hecho={tieneInsignia}
          accion={!tieneInsignia && <Button size="sm" variant="outline" onClick={() => navigate('/reventa/certificacion')}>Ir</Button>} />
      </div>

      <div className="space-y-3">
        <div>
          <h2 className="text-h3 font-display font-semibold text-neutral-900 dark:text-neutral-50">Cursos por especialidad</h2>
          <p className="text-body-sm text-neutral-500 dark:text-neutral-400">Cada track de certificación tiene un curso corto asociado en {esCd2b ? 'la academia cd-2b' : 'la Academia CoreDesk'}.</p>
        </div>
        <Card>
          <CardContent className="p-0 divide-y divide-neutral-100 dark:divide-neutral-800">
            {cursos.length === 0 && (
              <p className="p-5 text-body-sm text-neutral-500 dark:text-neutral-400">
                Todavía no hay cursos publicados de tu track. Mientras tanto puedes avanzar directo con la práctica en tu empresa demo.
              </p>
            )}
            {cursos.map((c) => {
              const e = enrollmentDe(c.id)
              const pct = e?.progress?.percentage || 0
              return (
                // La fila NO es un <button> entero: «Comenzar» es otro botón y un botón dentro de otro
                // es HTML inválido (el navegador los separa y el clic cae donde no toca).
                <div
                  key={c.id}
                  className={`flex items-center gap-3 p-4 sm:p-5 ${cursoActivoId === c.id ? 'bg-surface-muted' : ''}`}
                >
                  <button
                    type="button"
                    onClick={() => setCursoActivoId(c.id)}
                    aria-pressed={cursoActivoId === c.id}
                    className="flex min-w-0 flex-1 items-center gap-3.5 text-left"
                  >
                    <span className="h-10 w-10 rounded-lg bg-primary-50 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300 flex items-center justify-center flex-none">
                      <GraduationCap size={18} />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-body-sm font-medium text-foreground">{c.title}</span>
                      <span className="block text-caption text-subtle">
                        {(c.curriculum || []).reduce((s, m) => s + (m.lessons?.length || 0), 0)} lecciones · {c.totalDuration || 0} min
                      </span>
                      {e && (
                        <span className="mt-1.5 flex max-w-xs items-center gap-2">
                          <Progress value={pct} className="flex-1" />
                          <span className="font-mono text-caption text-subtle">{pct}%</span>
                        </span>
                      )}
                    </span>
                    {e && <Badge className="shrink-0">{pct >= 100 ? 'Completado' : 'Continuar'}</Badge>}
                  </button>
                  {!e && (
                    <Button size="sm" className="shrink-0" onClick={() => inscribirse(c)} disabled={inscribiendo === c.id}>
                      Comenzar
                    </Button>
                  )}
                </div>
              )
            })}
          </CardContent>
        </Card>

        {cursoActivo && (
          <Card>
            <CardContent className="p-0">
              <p className="px-5 pt-4 pb-2 text-caption uppercase tracking-wide text-neutral-400 dark:text-neutral-500">{cursoActivo.title} · lecciones</p>
              <div className="px-5 pb-4">
                {(cursoActivo.curriculum || []).map((mod, mi) => (
                  <div key={mi} className="mb-2">
                    <p className="text-caption font-semibold text-neutral-500 dark:text-neutral-400 mt-2">{mod.title}</p>
                    {(mod.lessons || []).map((l, li) => {
                      const done = lessonDone(enrollmentActivo, mi, li)
                      const enlace = enlaceDeLeccion(l)
                      return (
                        <div key={li} className="flex flex-wrap items-center gap-x-2.5 gap-y-1 py-2 border-b border-neutral-100 dark:border-neutral-800 last:border-0 text-body-sm">
                          {done ? <Check size={14} className="text-success-700 dark:text-success-300 flex-none" /> : <PlayCircle size={14} className="text-neutral-300 dark:text-neutral-600 flex-none" />}
                          <span className={`min-w-0 flex-1 ${done ? 'text-neutral-500 dark:text-neutral-400' : 'text-neutral-800 dark:text-neutral-200'}`}>{l.title}</span>
                          <span className="text-caption uppercase text-neutral-400 dark:text-neutral-500">{TIPO_LABEL[l.type] || l.type}</span>
                          {/* Estudiar no exige inscripción: «Ver» está siempre; inscribirse solo guarda el avance. */}
                          {urlIncrustable(l.videoUrl) ? (
                            <Button variant="outline" size="sm" onClick={() => setAbierta({ mi, li })}>
                              <PlayCircle size={14} /> Ver
                            </Button>
                          ) : enlace ? (
                            <Button asChild variant="outline" size="sm">
                              <a href={enlace} target="_blank" rel="noreferrer"><ExternalLink size={14} /> Abrir</a>
                            </Button>
                          ) : null}
                          {enrollmentActivo && !done && (
                            <Button variant="ghost" size="sm" onClick={() => completarLeccion(cursoActivo, enrollmentActivo, mi, li)}>
                              Marcar hecha
                            </Button>
                          )}
                        </div>
                      )
                    })}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {cursoActivo && (
          <VisorLeccion
            curso={cursoActivo}
            abierta={abierta}
            onAbrir={setAbierta}
            enrollment={enrollmentActivo}
            hecha={(mi, li) => lessonDone(enrollmentActivo, mi, li)}
            onMarcarHecha={(mi, li) => completarLeccion(cursoActivo, enrollmentActivo, mi, li)}
          />
        )}
      </div>

      <div className="space-y-3">
        <div>
          <h2 className="text-h3 font-display font-semibold text-neutral-900 dark:text-neutral-50">Documentación de referencia</h2>
          <p className="text-body-sm text-neutral-500 dark:text-neutral-400">Para consultar mientras cotizas — no hace falta leerla de corrido.</p>
        </div>
        <Card>
          <CardContent className="p-0 divide-y divide-neutral-100 dark:divide-neutral-800">
            {documentos.length === 0 && <p className="p-5 text-body-sm text-neutral-500 dark:text-neutral-400">Sin documentos publicados todavía.</p>}
            {documentos.map((d, i) => (
              <div key={i} className="flex items-center gap-3 p-4">
                <FileText size={18} className="text-accent-700 dark:text-accent-300 flex-none" />
                <div className="flex-1 min-w-0">
                  <p className="text-body-sm font-medium text-neutral-900 dark:text-neutral-50">{d.title}</p>
                  <p className="text-caption text-neutral-400 dark:text-neutral-500">{d.curso}</p>
                </div>
                {d.fileUrl ? (
                  <Button asChild size="sm" variant="outline"><a href={d.fileUrl} target="_blank" rel="noreferrer"><Download size={14} /> Abrir</a></Button>
                ) : (
                  <Button size="sm" variant="outline" disabled>Pendiente</Button>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      <div className="flex gap-3 items-start rounded-lg border border-primary-100 dark:border-primary-800 bg-primary-50 dark:bg-primary-900/20 px-4 py-3.5">
        <GraduationCap size={19} className="shrink-0 mt-0.5 text-primary-700 dark:text-primary-300" />
        <div>
          <p className="text-body-sm font-semibold text-neutral-900 dark:text-neutral-50">Practica antes de vender de verdad</p>
          <p className="text-body-sm text-neutral-600 dark:text-neutral-400 mt-1">
            Tu empresa demo tiene el catálogo completo, sin límite de tiempo. {esCd2b ? 'Cotiza y explora el panel' : 'Cotiza, aplica márgenes y explora el panel'} — nada de lo que hagas ahí se factura ni llega a un cliente.
          </p>
          <Button size="sm" className="mt-3 w-full sm:w-auto" onClick={() => navigate('/clientes')}>Abrir mi empresa demo</Button>
        </div>
      </div>
    </PageContainer>
  )
}

/**
 * Visor de una lección de video del curso activo: el video incrustado, «Marcar hecha» y «Siguiente».
 * Se puede ver sin estar inscrito; inscribirse («Comenzar») es lo que guarda el avance.
 */
function VisorLeccion({ curso, abierta, onAbrir, enrollment, hecha, onMarcarHecha }) {
  const [marcando, setMarcando] = useState(false)
  // Lecciones de video en orden, para «Siguiente».
  const videos = useMemo(() => {
    const out = []
    ;(curso.curriculum || []).forEach((m, mi) => (m.lessons || []).forEach((l, li) => {
      if (urlIncrustable(l.videoUrl)) out.push({ mi, li })
    }))
    return out
  }, [curso])

  const leccion = abierta ? curso.curriculum?.[abierta.mi]?.lessons?.[abierta.li] : null
  const src = leccion ? urlIncrustable(leccion.videoUrl) : null
  const pos = abierta ? videos.findIndex((v) => v.mi === abierta.mi && v.li === abierta.li) : -1
  const siguiente = pos >= 0 ? videos[pos + 1] : null
  const yaHecha = abierta ? hecha(abierta.mi, abierta.li) : false

  const marcar = async () => {
    setMarcando(true)
    try { await onMarcarHecha(abierta.mi, abierta.li) } finally { setMarcando(false) }
  }

  return (
    <Dialog open={!!src} onOpenChange={(o) => { if (!o) onAbrir(null) }}>
      {src && (
        <DialogContent className="sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle className="pr-8">{leccion.title}</DialogTitle>
            <DialogDescription>{curso.curriculum[abierta.mi]?.title}</DialogDescription>
          </DialogHeader>
          <div className="aspect-video w-full overflow-hidden rounded-lg bg-black">
            <iframe
              key={src}
              src={src}
              title={leccion.title}
              className="h-full w-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
              allowFullScreen
            />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {enrollment ? (
              yaHecha ? (
                <span className="inline-flex items-center gap-1.5 text-body-sm text-success-700 dark:text-success-300"><Check size={15} /> Lección hecha</span>
              ) : (
                <Button size="sm" onClick={marcar} disabled={marcando}><Check size={14} /> Marcar hecha</Button>
              )
            ) : (
              <span className="text-caption text-neutral-500 dark:text-neutral-400">Toca «Comenzar» en el curso para guardar tu avance.</span>
            )}
            <span className="flex-1" />
            {leccion.videoUrl && (
              <Button asChild variant="ghost" size="sm">
                <a href={leccion.videoUrl} target="_blank" rel="noreferrer"><ExternalLink size={14} /> Abrir aparte</a>
              </Button>
            )}
            {siguiente && (
              <Button variant="outline" size="sm" onClick={() => onAbrir(siguiente)}>Siguiente</Button>
            )}
          </div>
        </DialogContent>
      )}
    </Dialog>
  )
}

function PasoRuta({ n, titulo, desc, hecho, accion }) {
  return (
    <Card className={`p-4 flex gap-3 items-start ${hecho ? 'border-success-200 dark:border-success-800' : ''}`}>
      <span className={`h-6 w-6 rounded-full flex-none flex items-center justify-center text-caption font-mono font-semibold ${hecho ? 'bg-success-50 dark:bg-success-500/15 text-success-700 dark:text-success-300' : 'bg-primary-50 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300'}`}>
        {hecho ? <Check size={13} /> : n}
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-body-sm font-semibold text-neutral-900 dark:text-neutral-50">{n}. {titulo}</p>
        <p className="text-caption text-neutral-500 dark:text-neutral-400 mt-0.5">{desc}</p>
        {accion && <div className="mt-2">{accion}</div>}
      </div>
    </Card>
  )
}
